import { inRange } from 'lodash-es';
/* eslint frontbucket-patterns/no-new-sagas: "warn" */
import { Task } from 'redux-saga';
import {
  all,
  take,
  select,
  call,
  put,
  spawn,
  race,
  fork,
  TakeEffect,
} from 'redux-saga/effects';

import { ChunkEntry } from '@atlassian/bitkit-diff';

import { CodeReviewConversation } from 'src/components/conversation-provider/types';
import { UPDATE_GLOBAL_SHOULD_IGNORE_WHITESPACE_SUCCESS } from 'src/redux/diff-settings';
import { LoadGlobal } from 'src/redux/global/actions';
import {
  FETCH_COMMENTS,
  FETCH_DIFF,
  LOAD_DIFFSTAT,
  FETCH_COMMENT_CONTEXT,
  ENTERED_CODE_REVIEW,
  REFRESH_CODE_REVIEW_DATA_FROM_POLL_RESULTS,
  LOAD_SSR_COMMENTS_CONTEXT,
} from 'src/redux/pull-request/actions';
import {
  getCurrentPullRequestUrlPieces,
  getIsSingleFileModeActive,
  getRenderedPullRequestDiff,
  getPullRequestSourceHash,
  pullRequestRenderingLimits,
} from 'src/redux/pull-request/selectors';
import { isFileLevelComment } from 'src/redux/pull-request/utils/comments';
import { getConversations } from 'src/selectors/conversation-selectors';
import { Diff, PullRequestRenderingLimits } from 'src/types/pull-request';
import { hasRenderableLines } from 'src/utils/diff-classifications';
import { authRequest } from 'src/utils/fetch';
import {
  ContextLineRanges,
  validateContextLineRanges,
} from 'src/utils/validate-context-lines';

import { urls } from '../urls';

import { buildChunk, buildContextChunks } from './utils/build-a-chunk';
import { buildLineNumbers, parseSrcApiLines } from './utils/get-context-data';

type DiffAwareCommentFilter = (
  diffs: Diff[],
  isSingleFileModeActive: boolean,
  renderingLimits: PullRequestRenderingLimits | null
) => (conversation: CodeReviewConversation) => boolean;

type CommentWithClosestChunkMapper = (
  diffs: Diff[]
) => (conversation: CodeReviewConversation) => CommentWithClosestChunk;

type CommentWithClosestChunk = {
  conversation: CodeReviewConversation;
  chunk?: ChunkEntry;
};

const isIncompleteConversation = (conversation: CodeReviewConversation) =>
  !(conversation.meta.from !== null && conversation.meta.to !== null);

const none = (array: any[], predicate: (x: any) => boolean) =>
  !array.some(predicate);

const containsConversation =
  (conversation: CodeReviewConversation) => (chunk: ChunkEntry) => {
    // FROM | TO are the terms for the gutter column line numbers
    const fromStart = chunk.oldStart;
    const fromEnd = chunk.oldStart + chunk.oldLines - 1;
    const toStart = chunk.newStart;
    const toEnd = chunk.newStart + chunk.newLines - 1;

    const {
      from: convoFrom,
      to: convoTo,
      start_from: convoStartFrom,
      start_to: convoStartTo,
    } = conversation.meta;

    // This is redundant if the consumer has already checked this but this function doesn't know
    const isFileOrGlobalComment = convoFrom === null && convoTo === null;
    if (isFileOrGlobalComment) {
      return false;
    }

    // new logic for multi-line comments
    const isFromLineInChunk = convoFrom
      ? !!fromStart && inRange(convoFrom, fromStart, fromEnd + 1)
      : true;
    const isFromStartLineInChunk = convoStartFrom
      ? !!fromStart && inRange(convoStartFrom, fromStart, fromEnd + 1)
      : true;

    const isToLineInChunk = convoTo
      ? !!toStart && inRange(convoTo, toStart, toEnd + 1)
      : true;
    const isToStartLineInChunk = convoStartTo
      ? !!toStart && inRange(convoStartTo, toStart, toEnd + 1)
      : true;

    return (
      isFromLineInChunk &&
      isToLineInChunk &&
      isFromStartLineInChunk &&
      isToStartLineInChunk
    );
  };

const needsRendered: DiffAwareCommentFilter =
  (diffs, diffRenderingFlags, renderingLimits) => conversation => {
    const { path, outdated } = conversation.meta;
    const relevantDiff = diffs.find(diff => diff.to === path);
    if (!relevantDiff) {
      return false;
    }

    const { chunks } = relevantDiff;

    return (
      hasRenderableLines(relevantDiff, diffRenderingFlags, renderingLimits) &&
      !outdated &&
      !isFileLevelComment(conversation) &&
      none(chunks, containsConversation(conversation))
    );
  };

type MultilineConversationDiffMetadata = {
  chunks: ChunkEntry[];
  startChunkIndex: number;
  endChunkIndex: number;
  expandToFirstChunk: boolean;
  expandToLastChunk: boolean;
};

/**
 *
 * @param conversation The multi-line conversation we want to find in the diff
 * @param diffs The current Pull Request file diffs
 * @returns The indices of the start and end rendered diff chunks for the relevant conversation
 * and whether to expand from the comment start to the first chunk or the last chunk to the comment end
 */
const getMultilineConversationChunks = (
  conversation: CodeReviewConversation,
  diffs: Diff[]
): MultilineConversationDiffMetadata | undefined => {
  const { path } = conversation.meta;
  const relevantDiffIndex = diffs.findIndex(diff => diff.to === path);
  if (relevantDiffIndex === -1) {
    return undefined;
  }
  const { chunks } = diffs[relevantDiffIndex];

  const {
    from: convoFrom,
    to: convoTo,
    start_from: convoStartFrom,
    start_to: convoStartTo,
  } = conversation.meta;

  let expandToFirstChunk = false;
  // get the index of the first rendered diff chunk which contains part of the multi-line comment
  // if the comment starts above the chunk, set expandToFirstChunk to true as we will need to
  // later expand context lines from the comment start to this first chunk
  const startChunkIndex = chunks.findIndex(chunk => {
    const startFromAboveChunkEnd =
      !!convoStartFrom && convoStartFrom <= chunk.oldStart + chunk.oldLines;
    const startToAboveChunkEnd =
      !!convoStartTo && convoStartTo <= chunk.newStart + chunk.newLines;

    if (startFromAboveChunkEnd) {
      // see if the comment starts above the chunk (as opposed to within the chunk)
      expandToFirstChunk = convoStartFrom < chunk.oldStart;
      return true;
    }

    if (startToAboveChunkEnd) {
      // see if the comment starts above the chunk (as opposed to within the chunk)
      expandToFirstChunk = convoStartTo < chunk.newStart;
      return true;
    }

    return false;
  });

  // get the index of the last rendered diff chunk which contains part of the multi-line comment
  // we need to reverse the chunks to find the proper endChunkIndex because
  // otherwise we would capture the first chunk which includes either the from
  // or to line number, while the true end chunk may occur later with whichever
  // line number (to or from) comes later in the diff
  // if the comment ends below the chunk, set expandToLastChunk to true as we will need to
  // later expand context lines from the comment end to this last chunk
  let expandToLastChunk = false;
  const endChunkReverseIndex = chunks
    .slice()
    .reverse()
    .findIndex(chunk => {
      const fromBelowChunkStart = !!convoFrom && convoFrom >= chunk.oldStart;
      const toBelowChunkStart = !!convoTo && convoTo >= chunk.newStart;

      if (fromBelowChunkStart) {
        // see if the comment ends below the chunk (as opposed to within the chunk)
        expandToLastChunk = convoFrom > chunk.oldStart + chunk.oldLines;
        return true;
      }

      if (toBelowChunkStart) {
        // see if the comment ends below the chunk (as opposed to within the chunk)
        expandToLastChunk = convoTo > chunk.newStart + chunk.newLines;
        return true;
      }

      return false;
    });

  const endChunkIndex =
    endChunkReverseIndex === -1 ? -1 : chunks.length - endChunkReverseIndex - 1;

  // if we are unable to find the start chunk, return undefined unless the
  // MLC is fully contained below the last chunk (endChunk is the last chunk)
  if (startChunkIndex === -1 && endChunkIndex !== chunks.length - 1) {
    return undefined;
  }

  // if we are unable to find the end chunk, return undefined unless the
  // MLC is fully contained above the first chunk (startChunk is the first chunk)
  if (endChunkIndex === -1 && startChunkIndex !== 0) {
    return undefined;
  }

  return {
    chunks,
    startChunkIndex,
    endChunkIndex,
    expandToFirstChunk,
    expandToLastChunk,
  };
};

const getCommentWithClosestChunk: CommentWithClosestChunkMapper =
  diffs => conversation => {
    const { path } = conversation.meta;
    const relevantDiff = diffs.find(diff => diff.to === path)!;
    const { chunks } = relevantDiff;

    let minDistToChunk = Number.MAX_SAFE_INTEGER;
    let closestChunk: ChunkEntry;

    // find closest chunk above the convo if any
    chunks.forEach(chunk => {
      const { from: convoFrom, to: convoTo } = conversation.meta;
      // incomplete convo will have either a non-null "from" or "to" field
      if (convoFrom !== null) {
        const fromEnd = chunk.oldStart + chunk.oldLines - 1;
        if (convoFrom > fromEnd) {
          const currDist = Math.abs(fromEnd - convoFrom);
          if (currDist < minDistToChunk) {
            minDistToChunk = currDist;
            closestChunk = chunk;
          }
        }
      } else if (convoTo !== null) {
        const toEnd = chunk.newStart + chunk.newLines - 1;
        if (convoTo > toEnd) {
          const currDist = Math.abs(toEnd - convoTo);
          if (currDist < minDistToChunk) {
            minDistToChunk = currDist;
            closestChunk = chunk;
          }
        }
      }
    });

    return {
      conversation,
      // @ts-ignore closestChunk will be non-null
      chunk: closestChunk,
    };
  };

function* fetchContextLines(
  path: string,
  linesRange: ContextLineRanges,
  retries = 2
): Generator<any, void, any> {
  if (retries <= 0) {
    // Fail-safe because recursion
    return;
  }

  const sourceHash: string = yield select(getPullRequestSourceHash);

  const { owner, slug } = yield select(getCurrentPullRequestUrlPieces);

  try {
    validateContextLineRanges(linesRange);
  } catch {
    // Only logging these in Sentry for now
  }

  const url = urls.api.internal.contextFromSrcTopicDiff(
    {
      // lookup the source hash in the destination repo
      // to avoid issues with repo permissions
      owner,
      slug,
      sourceHash,
      filepath: path,
    },
    linesRange as ContextLineRanges
  );

  const response: Response = yield call(fetch, authRequest(url));

  const json =
    response.status === 200
      ? // @ts-ignore
        yield response.json()
      : // @ts-ignore
        yield Promise.resolve({ lines: [] });

  const lines = (json.data && parseSrcApiLines(json.data)) || [];

  const { startingFrom, startingTo } = linesRange as ContextLineRanges;
  const fromLineNumbers = buildLineNumbers(lines.length, startingFrom);
  const toLineNumbers = buildLineNumbers(lines.length, startingTo);

  const decodedLines = lines.map((line: string, index: number) => ({
    content: ` ${line}`, // diff content starts with +, - or ' '
    from_line: fromLineNumbers[index],
    to_line: toLineNumbers[index],
  }));

  yield put({
    type: FETCH_COMMENT_CONTEXT.SUCCESS,
    payload: {
      path,
      newChunks: [buildChunk(decodedLines)],
    },
  });
}

/**
 * Consolidates diff chunks for a multi-line conversation by fetching and building context chunks.
 *
 * This function handles different scenarios for multi-line conversations (MLC):
 * 1. MLC fully contained in a non-rendered context chunk - fetches lines between comment start/end (edge case)
 * 2. MLC spanning multiple rendered chunks - fetches context lines to connect start and end chunks and
 *  comment start/end lines if necessary
 *
 * @param conversationChunksMetadata - Metadata about the conversation's chunk positions and expansion flags
 * @param conversation - The multi-line conversation object containing comment metadata
 * @returns Array of ChunkEntry objects representing the consolidated chunks needed to render the conversation
 */
function* consolidateChunks(
  conversationChunksMetadata: MultilineConversationDiffMetadata,
  conversation: CodeReviewConversation
): Generator<any, ChunkEntry[], any> {
  // validate conversation metadata
  if (!conversation.meta.src_rev || !conversation.meta.path) {
    return [];
  }

  const {
    chunks,
    startChunkIndex,
    endChunkIndex,
    expandToFirstChunk,
    expandToLastChunk,
  } = conversationChunksMetadata;

  const mlcAboveFirstChunk =
    startChunkIndex === -1 && endChunkIndex === chunks.length - 1;
  const mlcBelowLastChunk = startChunkIndex === 0 && endChunkIndex === -1;
  const mlcInContextChunk =
    startChunkIndex === endChunkIndex + 1 ||
    mlcAboveFirstChunk ||
    mlcBelowLastChunk;

  // if the MLC is fully contained in a non-rendered context chunk, we can just
  // fetch the lines between the comment start and end lines and create a new chunk
  if (mlcInContextChunk) {
    const lines = yield call(
      fetchContext,
      conversation.meta.src_rev,
      conversation.meta.path,
      // since the MLC is fully contained in a context chunk,
      // we know the start_to and to fields are non-null
      conversation.meta.start_to!,
      conversation.meta.to!
    );
    const fromLineNumbers = buildLineNumbers(
      lines.length,
      conversation.meta.start_from!
    );
    const toLineNumbers = buildLineNumbers(
      lines.length,
      conversation.meta.start_to!
    );
    const commentLines = lines.map((line: string, index: number) => ({
      content: ` ${line}`, // diff content starts with +, - or ' '
      from_line: fromLineNumbers[index],
      to_line: toLineNumbers[index],
    }));
    return [buildChunk(commentLines)];
  }

  const startChunk = chunks[startChunkIndex];
  const endChunk = chunks[endChunkIndex];

  if (!startChunk || !endChunk) {
    return [];
  }

  let startLine = startChunk.newStart + startChunk.newLines;
  let endLine = endChunk.newStart - 1;

  // if we need to expand from the comment start to the first chunk,
  // override the start line to the comment start line
  if (expandToFirstChunk) {
    startLine = conversation.meta.start_to!;
  }

  // if we need to expand from the last chunk to the comment end,
  // override the end line to the comment end line
  if (expandToLastChunk) {
    endLine = conversation.meta.to!;
  }

  const lines = yield call(
    fetchContext,
    conversation.meta.src_rev!,
    conversation.meta.path!,
    startLine,
    endLine
  );

  // build all the new chunks necessary to render the multi-line comment
  const newChunks = buildContextChunks(
    lines,
    chunks.slice(startChunkIndex, endChunkIndex + 1),
    expandToFirstChunk
      ? startChunk.newStart - conversation.meta.start_to!
      : null,
    expandToLastChunk
      ? conversation.meta.to! - (endChunk.newStart + endChunk.newLines - 1)
      : null
  );

  return newChunks;
}

// These conversations will necessarily be in the "context lines" area
// So we can assume the delta between their FROM & TO line numbers
// is true of their neighboring line-number pairs.
// i.e. if this convo is on 4 | 5, then we know 3 | 4 and 5 | 6 should be
// safe to request so long as they don't cross into chunks we already render
// which are chunks this could contain diff changes.
export function* fetchConversationContext(
  conversationWithChunk: CommentWithClosestChunk
) {
  const EXTRA_LINES = 1; // line ranges are inclusive
  const { from, to, path } = conversationWithChunk.conversation.meta;
  let initialLineNums;

  // "incomplete" comment that needs special handling
  if (isIncompleteConversation(conversationWithChunk.conversation)) {
    // initial line numbers inferred from closest chunk above if any
    const { chunk } = conversationWithChunk;
    let offset = 0;
    if (chunk) {
      // chunk above
      const {
        oldStart: fromStart,
        newStart: toStart,
        oldLines: oldLines,
        newLines: newLines,
      } = chunk;
      offset = toStart - fromStart + (newLines - oldLines);
    }
    // @ts-ignore
    const correctedFrom = from === null ? to + -1 * offset : from;
    // @ts-ignore
    const correctedTo = to === null ? from + offset : to;

    initialLineNums = {
      startingFrom: Math.max(1, correctedFrom - EXTRA_LINES),
      startingTo: Math.max(1, correctedTo - EXTRA_LINES),
      endingFrom: correctedFrom + EXTRA_LINES,
      endingTo: correctedTo + EXTRA_LINES,
    };
  } else {
    initialLineNums = {
      // @ts-ignore FROM will be non null
      startingFrom: Math.max(1, from - EXTRA_LINES),
      // @ts-ignore TO will be non null
      startingTo: Math.max(1, to - EXTRA_LINES),
      // @ts-ignore FROM will be non null
      endingFrom: from + EXTRA_LINES,
      // @ts-ignore TO will be non null
      endingTo: to + EXTRA_LINES,
    };
  }

  yield* fetchContextLines(
    // @ts-ignore PATH will be non null
    path,
    initialLineNums
  );
}

function* userEntersCodeReviewPage(effects: TakeEffect[]) {
  yield take(ENTERED_CODE_REVIEW);
  yield all(effects);
}

function* userTogglesWhitespace(effects: TakeEffect[]) {
  yield take(UPDATE_GLOBAL_SHOULD_IGNORE_WHITESPACE_SUCCESS);
  yield all(effects);
}

function* userRefreshsPullRequestDiff(effects: TakeEffect[]) {
  yield take(REFRESH_CODE_REVIEW_DATA_FROM_POLL_RESULTS);
  yield all(effects);
}

export function* fetchCommentsContext() {
  const diffs: Diff[] = yield select(getRenderedPullRequestDiff);
  const conversations: CodeReviewConversation[] = yield select(
    getConversations
  );

  const isSingleFileModeActive: boolean = yield select(
    getIsSingleFileModeActive
  );

  const multiLineConversations = conversations.filter(
    convo => convo.meta.start_to || convo.meta.start_from
  );
  const singleLineConversations = conversations.filter(
    convo => !convo.meta.start_to && !convo.meta.start_from
  );

  // expand chunks for multiline conversations first
  const multilineConversationsToRender = multiLineConversations.filter(
    needsRendered(diffs, isSingleFileModeActive, pullRequestRenderingLimits)
  );

  const fileExpansionChunks: {
    [key: string]: { chunks: ChunkEntry[]; chunkIds: Set<string> };
  } = {};

  for (const multiLineConversation of multilineConversationsToRender) {
    const conversationChunksMetadata = getMultilineConversationChunks(
      multiLineConversation,
      diffs
    );

    const filepath = multiLineConversation.meta.path;
    const sourceHash = multiLineConversation.meta.src_rev;

    if (sourceHash && filepath && conversationChunksMetadata) {
      // grab the context chunks needed to connect the start and end chunks of the multi-line comment
      const expansionChunks: ChunkEntry[] = yield call(
        consolidateChunks,
        conversationChunksMetadata,
        multiLineConversation
      );
      if (filepath in fileExpansionChunks) {
        // filter out chunks that have already been identified as necessary to render the file multi-line comments
        const newExpansionChunks = expansionChunks.filter(
          chunk => !fileExpansionChunks[filepath].chunkIds.has(chunk.id)
        );
        fileExpansionChunks[filepath].chunks.push(...newExpansionChunks);
        newExpansionChunks.forEach(chunk => {
          fileExpansionChunks[filepath].chunkIds.add(chunk.id);
        });
      } else {
        fileExpansionChunks[filepath] = {
          chunks: expansionChunks,
          chunkIds: new Set(expansionChunks.map(chunk => chunk.id)),
        };
      }
    }
  }
  for (const [filename, value] of Object.entries(fileExpansionChunks)) {
    yield put({
      type: FETCH_COMMENT_CONTEXT.SUCCESS,
      payload: {
        path: filename,
        newChunks: value.chunks,
      },
    });
  }

  // refetch the diff from redux to ensure that any chunk transformation in the multi-line
  // comment rendering process above has been applied and the logic below will reference the
  // latest diff
  const updatedDiffs: Diff[] = yield select(getRenderedPullRequestDiff);
  const singleLineConversationsToRender = singleLineConversations
    .filter(
      needsRendered(
        updatedDiffs,
        isSingleFileModeActive,
        pullRequestRenderingLimits
      )
    )
    .map(getCommentWithClosestChunk(updatedDiffs));
  const singleLineContextTasks = singleLineConversationsToRender.map(
    conversationToRender =>
      spawn(fetchConversationContext, conversationToRender)
  );
  yield all(singleLineContextTasks);
}

export function* commentsContextSaga() {
  let task: Task | undefined;
  // wait for features to be set in redux
  yield take([LoadGlobal.SUCCESS, LOAD_SSR_COMMENTS_CONTEXT]);

  const effects = [take(FETCH_DIFF.SUCCESS), take(LOAD_DIFFSTAT.SUCCESS)];
  while (true) {
    yield race([
      call(
        userEntersCodeReviewPage,
        [take(FETCH_COMMENTS.SUCCESS)].concat(effects)
      ),
      call(userTogglesWhitespace, effects),
      call(userRefreshsPullRequestDiff, effects),
    ]);

    if (task !== undefined && task.isRunning()) {
      yield task.cancel();
    }

    task = yield fork(fetchCommentsContext);
  }
}

/**
 * Fetches context lines from the source repository for a specific file and line range.
 *
 * This function is used to retrieve the actual file content between specified line numbers
 * from the source commit. It's primarily used for expanding context around multi-line
 * comments that span multiple diff chunks or are in non-rendered areas.
 *
 * @param sourceHash - The commit hash to fetch context lines from
 * @param filepath - The file path to fetch context lines for
 * @param startLine - The starting line number (inclusive)
 * @param endLine - The ending line number (inclusive)
 * @returns Array of strings representing the file content lines, or empty array if request fails
 */
export function* fetchContext(
  sourceHash: string,
  filepath: string,
  startLine: number,
  endLine: number
) {
  const { owner, slug: repoSlug } = yield select(
    getCurrentPullRequestUrlPieces
  );

  const url = urls.api.internal.contextFromSrcTopicDiff(
    {
      // lookup the source hash in the destination repo
      // to avoid issues with repo permissions
      owner,
      slug: repoSlug,
      sourceHash,
      filepath,
    },
    {
      // start at the end of the comment start chunk
      startingTo: startLine,
      // end at the start of the comment end chunk
      endingTo: endLine,
    } as ContextLineRanges
  );

  const response: Response = yield call(fetch, authRequest(url));

  // silently fail to expand the context chunks if the request fails
  // the multi-line comment would not be rendered in the diff in this case
  if (!response.ok) {
    return [];
  }
  // @ts-ignore
  const resp = yield response.json();
  const lines = (resp.data && parseSrcApiLines(resp.data)) || [];
  return lines;
}
